import { useMemo, useState } from 'react';
import { Calendar, CheckCircle2, Filter, Search, X, XCircle } from 'lucide-react';
import {
  BosDurum,
  Buton,
  Girdi,
  OnayDialog,
  Rozet,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  toast,
  type ColumnDef,
  type RozetTonu,
  type TarihAraligi,
} from '@emanetx/ui';
import type { Rezervasyon, RezervasyonDurumu } from '@emanetx/sdk';
import { telefonMaskele } from '../kiralamalar/durumlar.js';
import { useRezervasyonIptal, useRezervasyonListesi } from './api.js';

const DURUM_TONU: Record<RezervasyonDurumu, RozetTonu> = {
  BEKLEMEDE: 'turuncu',
  KULLANILDI: 'yesil',
  IPTAL_EDILDI: 'gri',
  SURESI_GECTI: 'kirmizi',
};

const DURUM_ETIKET: Record<RezervasyonDurumu, string> = {
  BEKLEMEDE: 'Beklemede',
  KULLANILDI: 'Kullanıldı',
  IPTAL_EDILDI: 'İptal',
  SURESI_GECTI: 'Süresi Geçti',
};

function sonOtuzGun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function SayfaRezervasyonlar() {
  const [sayfa, setSayfa] = useState(1);
  const [arama, setArama] = useState('');
  const [durum, setDurum] = useState<RezervasyonDurumu | ''>('');
  const [araliki, setAralik] = useState<TarihAraligi>(sonOtuzGun);
  const [iptalOnay, setIptalOnay] = useState<Rezervasyon | null>(null);

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(arama && { arama }),
    ...(durum && { durum: durum as RezervasyonDurumu }),
    ...(tarihIso(araliki.baslangic) && { baslangicTen: tarihIso(araliki.baslangic) }),
    ...(tarihIso(araliki.bitis) && { baslangicAKadar: tarihIso(araliki.bitis) }),
  };

  const liste = useRezervasyonListesi(sorgu);
  const iptal = useRezervasyonIptal();

  const kolonlar = useMemo<ColumnDef<Rezervasyon, unknown>[]>(
    () => [
      {
        accessorKey: 'baslangicZamani',
        header: 'Başlangıç',
        meta: { etiket: 'Başlangıç' },
        cell: ({ row }) => (
          <div className="text-xs">
            <p className="text-fume-900 dark:text-fume-50 font-medium">
              {new Date(row.original.baslangicZamani).toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'short',
              })}
            </p>
            <p className="text-fume-500 font-mono">
              {new Date(row.original.baslangicZamani).toLocaleTimeString('tr-TR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        ),
      },
      {
        id: 'musteri',
        header: 'Müşteri',
        meta: { etiket: 'Müşteri' },
        cell: ({ row }) => (
          <div>
            <div className="text-fume-700 dark:text-fume-300 font-mono text-xs">
              {row.original.musteriTelefon ? telefonMaskele(row.original.musteriTelefon) : '—'}
            </div>
            {row.original.musteriAdSoyad && (
              <div className="text-fume-400 text-xs">{row.original.musteriAdSoyad}</div>
            )}
          </div>
        ),
      },
      {
        id: 'noktaDolap',
        header: 'Nokta / Dolap',
        meta: { etiket: 'Nokta / Dolap' },
        cell: ({ row }) => (
          <span className="text-xs">
            <span className="text-fume-500 font-mono">{row.original.noktaKod ?? '—'}</span>
            {row.original.dolapNumara && (
              <span className="text-fume-700 dark:text-fume-300 font-mono">
                {' / #'}
                {row.original.dolapNumara}
              </span>
            )}
          </span>
        ),
      },
      {
        accessorKey: 'sureSaat',
        header: 'Süre',
        meta: { etiket: 'Süre' },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.sureSaat} saat</span>,
      },
      {
        accessorKey: 'onOdemeTutar',
        header: 'Ön Ödeme',
        meta: { etiket: 'Ön Ödeme' },
        cell: ({ row }) => {
          const sembol =
            row.original.paraBirimi === 'TRY' ? '₺' : row.original.paraBirimi === 'EUR' ? '€' : '$';
          return (
            <span className="font-mono text-xs">
              {sembol}
              {row.original.onOdemeTutar}
            </span>
          );
        },
        sortingFn: (a, b) => Number(a.original.onOdemeTutar) - Number(b.original.onOdemeTutar),
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
      {
        id: 'aksiyon',
        header: '',
        meta: { etiket: 'Aksiyon' },
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          if (r.durum === 'BEKLEMEDE') {
            return (
              <Buton
                stil="cizgili"
                boyut="kucuk"
                onClick={(e) => {
                  e.stopPropagation();
                  setIptalOnay(r);
                }}
              >
                <X size={12} /> İptal
              </Buton>
            );
          }
          if (r.durum === 'KULLANILDI') {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 size={12} /> Kiralandı
              </span>
            );
          }
          if (r.durum === 'SURESI_GECTI') {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                <XCircle size={12} /> Gelmedi
              </span>
            );
          }
          return null;
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Rezervasyonlar</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Mobil uygulamadan oluşturulan dolap rezervasyonları.
        </p>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={Calendar}
            baslik="Bu tarih aralığında rezervasyon yok"
            aciklama="Tarih aralığını veya filtreyi değiştirin."
          />
        }
        ustAksiyonlar={
          <>
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                size={14}
                className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="Müşteri telefon, ad veya dolap..."
                value={arama}
                onChange={(e) => {
                  setSayfa(1);
                  setArama(e.target.value);
                }}
                className="h-10 pl-9 text-sm"
              />
            </div>
            <TarihAraligiSecici
              deger={araliki}
              onDegisim={(a) => {
                setAralik(a);
                setSayfa(1);
              }}
              etiketYok
              className="h-10 text-xs"
            />
            <div className="flex items-center gap-2">
              <Filter size={13} className="text-fume-400" />
              <select
                value={durum}
                onChange={(e) => {
                  setDurum(e.target.value as RezervasyonDurumu | '');
                  setSayfa(1);
                }}
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
              >
                <option value="">Tüm durumlar</option>
                <option value="BEKLEMEDE">Beklemede</option>
                <option value="KULLANILDI">Kullanıldı</option>
                <option value="IPTAL_EDILDI">İptal</option>
                <option value="SURESI_GECTI">Süresi Geçti</option>
              </select>
            </div>
          </>
        }
      />

      <OnayDialog
        acik={!!iptalOnay}
        onDegisim={(o) => !o && setIptalOnay(null)}
        baslik="Rezervasyon iptal edilsin mi?"
        aciklama={
          iptalOnay
            ? `${iptalOnay.musteriAdSoyad ?? 'Müşteri'} — ${iptalOnay.noktaKod ?? ''} / #${iptalOnay.dolapNumara ?? ''}. Ön ödeme iade prosedürü tetiklenecek.`
            : ''
        }
        onayMetni="Evet, iptal et"
        iptalMetni="Vazgeç"
        tur="uyari"
        onOnay={async () => {
          if (!iptalOnay) return;
          try {
            await iptal.mutateAsync(iptalOnay.id);
            toast.success('Rezervasyon iptal edildi');
            setIptalOnay(null);
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
      />
    </div>
  );
}
